import React, { useCallback, useContext } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';

import store from '../../stores/stencilBuilder';

import StencilFormCreate from './StencilFormCreate';
import StencilFormPreview from './StencilFormPreview';

import type { TStencilBuilderProps, TStencilStore } from '../../types/Stencil';
import MonitorUnload from './MonitorUnload';
import { TextEditorProvider } from '@src/contexts/wysiwyg/text_editor';
import {
  WysiwygEditorProvider,
  WysiwygEditorStateContext
} from '@src/contexts/wysiwyg/editor';
import { RailsContext } from '@src/types/react-on-rails';
import { TWysiwygSettings } from '@src/types/wysiwyg_editor';
import { PageContentProvider } from '@src/contexts/stencil/page_content';
import { setCurrentPage } from '@src/actions/stencils/editor';

function StencilBuilder() {
  const dispatch = useDispatch();
  const { activePage } = useContext(WysiwygEditorStateContext);
  const handleShowCreate = useCallback(() => {
    dispatch(setCurrentPage('build'));
  }, [dispatch]);

  const handleShowPreview = useCallback(() => {
    dispatch(setCurrentPage('preview'));
  }, [dispatch]);

  if (activePage === 'preview') {
    return <StencilFormPreview onPreviousStep={handleShowCreate} />;
  } else {
    return <StencilFormCreate onNextStep={handleShowPreview} />;
  }
}

function WysiwygEditor(props: TWysiwygSettings) {
  const currentView = useSelector(
    (state: TStencilStore) => state.pageState.currentView
  );

  const isDirty = useSelector(
    (state: TStencilStore) => state.pageState.isDirty
  );

  return (
    <WysiwygEditorProvider
      {...props}
      activePage={currentView}
      pageIsDirty={isDirty}
    >
      <PageContentProvider>
        <TextEditorProvider>
          <MonitorUnload />
          <StencilBuilder />
        </TextEditorProvider>
      </PageContentProvider>
    </WysiwygEditorProvider>
  );
}

export default (props: TStencilBuilderProps, railsContext: RailsContext) => (
  <Provider store={store(props.initialStore, railsContext)}>
    <WysiwygEditor {...props.wysiwygSettings} />
  </Provider>
);
